import {objectFromEntries} from '@benchling/js-utils';
import {useState} from 'react';

import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import getParameterValueObject from 'insights/parameters/data/getParameterValueObject';
import {JSONValue} from 'util/Types';

import {AnalyticsParameterConfigGraphQL as AnalyticsParameterConfig} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {VALUE_VALIDATORS} from './data/ValueValidators';
import {CONFIG_TYPENAME_TO_PARAMETER_TYPE} from './ParameterConstants';

interface UseAnalyticsParametersStateOutput {
  clearAllValues: () => void;
  configs: {[configId: string]: AnalyticsParameterConfig};
  valueStates: {[configId: string]: {value: AnalyticsParameterValue; isValid: boolean}};
  updateConfig: (newConfig: AnalyticsParameterConfig) => void;
  updateValue: (configId: string, valueJson: JSONValue) => void;
}

export default function useAnalyticsParametersState(
  configs: Array<AnalyticsParameterConfig>,
  values: Array<AnalyticsParameterValue>
): UseAnalyticsParametersStateOutput {
  // ensure that each config has a corresponding value definition
  const filledValues = configs.map<AnalyticsParameterValue>(
    (config) => values.find((value) => value.config.id === config.id) ?? getParameterValueObject(config, null)
  );

  const [parameterState, setParameterState] = useState({
    configs: objectFromEntries<AnalyticsParameterConfig>(configs.map((config) => [config.id, config])),
    valueStates: objectFromEntries(filledValues.map((value) => [value.config.id, {value, isValid: true}])),
  });

  function updateConfig(newConfig: AnalyticsParameterConfig): void {
    const oldValueState = parameterState.valueStates[newConfig.id];
    const parameterType = CONFIG_TYPENAME_TO_PARAMETER_TYPE[newConfig.__typename];
    const validatorFn = VALUE_VALIDATORS[parameterType];
    const newIsValid = validatorFn(oldValueState.value?.valueJson);
    let newValueJson = newIsValid ? oldValueState.value?.valueJson : null;

    if (newConfig.isMulti && !Array.isArray(newValueJson)) {
      newValueJson = newValueJson == null ? [] : [newValueJson];
    } else if (!newConfig.isMulti && Array.isArray(newValueJson)) {
      newValueJson = newValueJson.length > 0 ? newValueJson[0] : null;
    }

    setParameterState({
      configs: {
        ...parameterState.configs,
        [newConfig.id]: newConfig,
      },
      valueStates: {
        ...parameterState.valueStates,
        [newConfig.id]: {
          value: getParameterValueObject(newConfig, newValueJson),
          isValid: true,
        },
      },
    });
  }

  function updateValue(configId: string, valueJson: JSONValue): void {
    const oldValueState = parameterState.valueStates[configId];
    const newIsValid = validateAgainstConfig(configId, valueJson);
    const newValue = {
      value: {
        ...oldValueState.value,
        valueJson,
      },
      isValid: newIsValid,
    };

    setParameterState({
      configs: {...parameterState.configs},
      valueStates: {
        ...parameterState.valueStates,
        [configId]: newValue,
      },
    });
  }

  function clearAllValues(): void {
    const newValueStates = objectFromEntries(
      Object.entries(parameterState.valueStates).map(([configId, state]) => [
        configId,
        {
          value: {...state.value, valueJson: null},
          isValid: true,
        },
      ])
    );

    setParameterState({
      configs: parameterState.configs,
      valueStates: newValueStates,
    });
  }

  function validateAgainstConfig(configId: string, valueJson: JSONValue): boolean {
    const config = parameterState.configs[configId];
    const parameterType = CONFIG_TYPENAME_TO_PARAMETER_TYPE[config.__typename];
    const validatorFn = VALUE_VALIDATORS[parameterType];

    return validatorFn(valueJson);
  }

  return {
    updateConfig,
    updateValue,
    clearAllValues,
    configs: parameterState.configs,
    valueStates: parameterState.valueStates,
  };
}
